import { useEffect, useState } from "react";
import { X, CurrencyDollar, Percent, CheckCircle, Receipt } from "@phosphor-icons/react";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin-only price adjustment modal. Three actions in one place:
 *   • discount_total    — reduce project total by flat or percent
 *   • credit_balance    — leave total alone, write down the remaining balance
 *   • mark_paid_in_full — set paid_amount = total, status = paid (waives residual)
 *
 * Live preview of the resulting total / paid / balance updates as the admin types
 * so they can see exactly what the client will see before committing.
 */
export function AdminAdjustPriceModal({ open, onClose, project, onSaved }) {
  const [action, setAction] = useState("discount_total");
  const [discountType, setDiscountType] = useState("flat");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset every time the modal opens fresh
      setAction("discount_total");
      setDiscountType("flat");
      setAmount("");
      setNote("");
    }
  }, [open]);

  if (!open || !project) return null;

  const total = Number(project.total_usd || 0);
  const paid = Number(project.paid_amount || 0);
  const balance = Math.max(0, total - paid);
  const numAmount = Number(amount || 0);

  // Live preview of the after-state
  let newTotal = total;
  let newPaid = paid;
  let applied = 0;

  if (action === "discount_total" && numAmount > 0) {
    const target = discountType === "percent" ? total * (1 - numAmount / 100) : total - numAmount;
    newTotal = Math.max(paid, Math.round(target * 100) / 100);
    applied = Math.round((total - newTotal) * 100) / 100;
  } else if (action === "credit_balance" && numAmount > 0) {
    const credit = discountType === "percent" ? balance * (numAmount / 100) : Math.min(balance, numAmount);
    applied = Math.round(credit * 100) / 100;
    newPaid = Math.round((paid + applied) * 100) / 100;
  } else if (action === "mark_paid_in_full") {
    applied = balance;
    newPaid = total;
  }

  const newBalance = Math.max(0, newTotal - newPaid);
  const willMakePaid = newBalance <= 0.01 && newTotal > 0;

  const validate = () => {
    if (action === "mark_paid_in_full") return balance > 0 || "Already paid in full";
    if (!amount || numAmount <= 0) return "Amount is required";
    if (discountType === "percent" && (numAmount <= 0 || numAmount > 100)) return "Percent must be 1-100";
    if (action === "discount_total" && discountType === "flat" && numAmount > total) return "Flat discount can't exceed total";
    if (action === "credit_balance" && balance <= 0) return "No outstanding balance to credit";
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v !== true) return toast.error(v);
    setSaving(true);
    try {
      const body = { action, note: note.trim() || undefined };
      if (action !== "mark_paid_in_full") {
        body.discount_type = discountType;
        body.amount = discountType === "percent" ? numAmount / 100 : numAmount;
      }
      const { data } = await api.patch(`/admin/projects/${project.id}/adjust-price`, body);
      toast.success(
        action === "mark_paid_in_full" ? "Marked paid in full"
        : action === "discount_total" ? `Discount applied — saved ${formatUSD(applied)}`
        : `Credit applied — ${formatUSD(applied)} off balance`
      );
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to apply");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="adjust-price-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-[#262626]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Pricing</div>
            <div className="font-display text-xl mt-1">Adjust price</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Current state */}
          <div className="grid grid-cols-3 gap-px bg-[#262626] border border-[#262626]" data-testid="adjust-current-state">
            <Cell label="Total" value={formatUSD(total)} />
            <Cell label="Paid" value={formatUSD(paid)} />
            <Cell label="Balance" value={formatUSD(balance)} accent={balance > 0} />
          </div>

          {/* Action selector */}
          <div className="space-y-2" role="radiogroup" aria-label="Adjustment action">
            <Radio
              checked={action === "discount_total"}
              onChange={() => setAction("discount_total")}
              testId="adjust-action-discount-total"
              icon={<Percent size={14} />}
              title="Reduce project total"
              desc="Lowers the total amount the client owes. Existing deposit stays paid."
            />
            <Radio
              checked={action === "credit_balance"}
              onChange={() => setAction("credit_balance")}
              testId="adjust-action-credit-balance"
              icon={<CurrencyDollar size={14} />}
              title="Credit the remaining balance"
              desc="Total stays the same; reduces what's still owed."
              disabled={balance <= 0}
            />
            <Radio
              checked={action === "mark_paid_in_full"}
              onChange={() => setAction("mark_paid_in_full")}
              testId="adjust-action-mark-paid"
              icon={<CheckCircle size={14} weight="fill" />}
              title="Mark paid in full"
              desc="Waives the remaining balance. Total stays the same."
              disabled={balance <= 0}
            />
          </div>

          {/* Amount inputs (hidden for mark_paid_in_full) */}
          {action !== "mark_paid_in_full" && (
            <div className="grid grid-cols-2 gap-3" data-testid="adjust-amount-row">
              <Field label="Type">
                <div className="grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
                  <button
                    type="button"
                    onClick={() => setDiscountType("flat")}
                    data-testid="adjust-type-flat"
                    className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${discountType === "flat" ? "bg-[#FF2D2D] text-white" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}
                  >
                    Flat $
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiscountType("percent")}
                    data-testid="adjust-type-percent"
                    className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${discountType === "percent" ? "bg-[#FF2D2D] text-white" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}
                  >
                    Percent %
                  </button>
                </div>
              </Field>
              <Field label={discountType === "flat" ? "Amount off ($)" : "Percent off (%)"}>
                <div className="relative">
                  {discountType === "flat" && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a3a3a3] text-sm">$</span>
                  )}
                  <input
                    type="number"
                    min={discountType === "flat" ? "0.01" : "1"}
                    max={discountType === "flat" ? undefined : "100"}
                    step={discountType === "flat" ? "0.01" : "1"}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    data-testid="adjust-amount-input"
                    placeholder={discountType === "flat" ? "50.00" : "20"}
                    className={`w-full bg-[#050505] border border-[#262626] py-2 text-white tabular-nums focus:border-[#FF2D2D] outline-none ${discountType === "flat" ? "pl-7 pr-3" : "px-3"}`}
                  />
                </div>
              </Field>
            </div>
          )}

          <Field label="Reason (optional, shown to client)">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              data-testid="adjust-note-input"
              placeholder="e.g. Holiday courtesy"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
            />
          </Field>

          {/* Live after-state */}
          {applied > 0 && (
            <div className="border border-[#FF2D2D] bg-[#FF2D2D]/5 p-4 space-y-2" data-testid="adjust-preview">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">After this adjustment</div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase text-[#a3a3a3] tracking-wider">Total</div>
                  <div className="font-display text-lg tabular-nums">{formatUSD(newTotal)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#a3a3a3] tracking-wider">Paid</div>
                  <div className="font-display text-lg tabular-nums">{formatUSD(newPaid)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#a3a3a3] tracking-wider">Balance</div>
                  <div className={`font-display text-lg tabular-nums ${newBalance > 0 ? "text-[#FF2D2D]" : "text-[#28C878]"}`}>
                    {formatUSD(newBalance)}
                  </div>
                </div>
              </div>
              <div className="text-xs text-[#a3a3a3] flex items-center gap-1.5">
                <Receipt size={11} /> Client saves <span className="text-white font-semibold tabular-nums">{formatUSD(applied)}</span>
                {willMakePaid && <span className="text-[#28C878] uppercase tracking-wider text-[9px] ml-1">— marks PAID</span>}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} className="btn-outline px-4 py-2 text-xs uppercase tracking-wider">Cancel</button>
            <button
              type="submit"
              disabled={saving || applied <= 0}
              data-testid="adjust-submit"
              className="btn-primary px-5 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Apply adjustment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div className="bg-[#0a0a0a] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3]">{label}</div>
      <div className={`font-display text-lg mt-0.5 tabular-nums ${accent ? "text-[#FF2D2D]" : ""}`}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">{label}</label>
      {children}
    </div>
  );
}

function Radio({ checked, onChange, testId, icon, title, desc, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      data-testid={testId}
      className={`w-full text-left p-3 border transition-colors flex items-start gap-3 ${
        checked
          ? "border-[#FF2D2D] bg-[#FF2D2D]/5"
          : disabled
            ? "border-[#262626] opacity-40 cursor-not-allowed"
            : "border-[#262626] hover:border-[#a3a3a3] cursor-pointer"
      }`}
    >
      <span className={`mt-0.5 ${checked ? "text-[#FF2D2D]" : "text-[#a3a3a3]"}`}>{icon}</span>
      <span className="flex-1">
        <span className="block text-sm text-white">{title}</span>
        <span className="block text-xs text-[#a3a3a3] mt-0.5">{desc}</span>
      </span>
      <span
        className={`w-3.5 h-3.5 rounded-full border mt-1 ${
          checked ? "border-[#FF2D2D] bg-[#FF2D2D]" : "border-[#a3a3a3]"
        }`}
      />
    </button>
  );
}
